"use client";

import type { ReactNode } from "react";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";

/** How wide the fade over a side that still has content is. */
const FADE_PX = 32;

/**
 * A horizontally scrolling strip that says how much of it is off-screen.
 *
 * The invariant: a rail never shows a raw native scrollbar and never clips an
 * item with nothing to say so. Overflow is announced twice — a fade over the
 * side that still has content, and, where a pointer can hover, a button that
 * moves the strip by roughly a screen.
 *
 * ⚠️ `scrollLeft` is NEGATIVE in RTL — the spec-compliant behaviour every
 * current engine ships — so every measurement here goes through `Math.abs()`
 * and every scroll carries the direction's sign. Direction is read off the
 * element's own computed style rather than the locale, because the design
 * editor's preview mounts a storefront subtree in whichever direction the
 * merchant is previewing, not the one the panel is in.
 *
 * The fade is a `mask-image`, not a gradient overlay in the surface colour:
 * an overlay has to name a background, and the storefront's background is a
 * per-store token that also flips with `themeMode`. A mask is whatever is
 * behind it.
 */
export function ScrollRail({
	children,
	label,
	className,
	contentClassName,
	arrowClassName = "top-1/2 -translate-y-1/2",
	snap = false,
	reveal = null,
}: {
	children: ReactNode;
	/** Names what the arrows move, for their accessible labels. */
	label: { prev: string; next: string };
	/** The positioning context the fades and arrows are absolute to. */
	className?: string;
	/** The scrolling row itself — gap, padding and item sizing belong here. */
	contentClassName?: string;
	/** Where the arrows sit vertically. Tiles with a label below want the tile's centre. */
	arrowClassName?: string;
	/** Snap to item edges. For a rail of equal tiles, not a row of text links. */
	snap?: boolean;
	/**
	 * Bring the child carrying `data-rail-reveal` into view whenever this value
	 * changes. It is a KEY, not a flag: a rail arrives already scrolled to zero
	 * on every navigation, so what the rail has to answer is "which item is the
	 * page about now", and the value that names it is the only thing that can
	 * tell a second render of the same state from a move to another one.
	 */
	reveal?: string | null;
}) {
	const ref = useRef<HTMLDivElement>(null);
	// LOGICAL edges: `start` means more content toward the reading start.
	const [edges, setEdges] = useState({ start: false, end: false, rtl: false });

	const measure = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		const rtl = getComputedStyle(el).direction === "rtl";
		const pos = Math.abs(el.scrollLeft);
		const max = el.scrollWidth - el.clientWidth;
		setEdges({ start: pos > 1, end: pos < max - 1, rtl });
	}, []);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		// The first measurement comes from the observer's own initial callback,
		// which fires as soon as it observes — so there is no synchronous
		// `setState` in the effect, and no render that assumed no overflow.
		// After that it catches a viewport resize and a font swap; a change of
		// `children` re-runs the effect, which covers the content side.
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [measure, children]);

	/*
	 * A shopper who opens a link with a category already chosen must be able to
	 * SEE which one it is. The rail starts at its reading start, and a category
	 * that sits past the fold is announced only by `aria-current` and a bold
	 * label nobody can reach without swiping.
	 *
	 * Centred, not merely dragged into view, because the items on either side
	 * are the siblings the rail exists to offer. Instant rather than smooth:
	 * this runs on arrival, where a scroll animation reads as the page moving
	 * under the shopper rather than as a response to anything they did.
	 *
	 * ⚠️ `scrollBy` with a delta measured off two `getBoundingClientRect`s, not
	 * `scrollLeft =` and not `scrollIntoView`. A delta is physical pixels and so
	 * needs none of the RTL sign handling `measure()` carries, and unlike
	 * `scrollIntoView` it cannot scroll an ANCESTOR: on a deep link the rail is
	 * below the hero, and scrolling the page to it would move a shopper who was
	 * looking at the catalogue.
	 */
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const target = el.querySelector<HTMLElement>("[data-rail-reveal]");
		if (!target) return;
		const box = el.getBoundingClientRect();
		const item = target.getBoundingClientRect();
		// Already whole and in view: leave the scroll where the shopper put it.
		if (item.left >= box.left - 1 && item.right <= box.right + 1) return;
		el.scrollBy({ left: item.left - box.left - (box.width - item.width) / 2 });
	}, [reveal]);

	function nudge(direction: 1 | -1) {
		const el = ref.current;
		if (!el) return;
		const sign = edges.rtl ? -1 : 1;
		el.scrollBy({ left: sign * direction * el.clientWidth * 0.8, behavior: "smooth" });
	}

	// Physical sides, for the mask. The logical start is on the left only in LTR.
	const fadeLeft = edges.rtl ? edges.end : edges.start;
	const fadeRight = edges.rtl ? edges.start : edges.end;
	const mask =
		fadeLeft || fadeRight
			? `linear-gradient(to right, ${[
					fadeLeft ? `transparent 0, #000 ${FADE_PX}px` : "#000 0",
					fadeRight ? `#000 calc(100% - ${FADE_PX}px), transparent 100%` : "#000 100%",
				].join(", ")})`
			: undefined;

	return (
		<div className={`relative ${className ?? ""}`}>
			<div
				ref={ref}
				onScroll={measure}
				className={`rail ${snap ? "rail-snap" : ""} ${contentClassName ?? ""}`}
				style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
			>
				{children}
			</div>

			{/*
			  Hidden below `sm`, where the gesture is a swipe and a button over the
			  first item costs more than it gives. `hover:` alone would not do it:
			  Tailwind v4 gates that variant behind `@media (hover: hover)`, so the
			  button would still occupy the strip on a touch screen, just without a
			  hover state.
			*/}
			{edges.start && (
				<RailArrow
					onClick={() => nudge(-1)}
					ariaLabel={label.prev}
					icon={faChevronLeft}
					className={`start-0 ${arrowClassName}`}
				/>
			)}
			{edges.end && (
				<RailArrow
					onClick={() => nudge(1)}
					ariaLabel={label.next}
					icon={faChevronRight}
					className={`end-0 ${arrowClassName}`}
				/>
			)}
		</div>
	);
}

function RailArrow({
	onClick,
	ariaLabel,
	icon,
	className,
}: {
	onClick: () => void;
	ariaLabel: string;
	icon: typeof faChevronLeft;
	className: string;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={ariaLabel}
			className={`absolute z-10 hidden size-9 items-center justify-center rounded-full border border-border bg-surface/85 text-muted-foreground shadow-sm backdrop-blur transition hover:bg-surface-2 hover:text-foreground sm:flex ${className}`}
		>
			{/* `chevron-left` / `chevron-right` carry a direction segment, so
			    `Icon`'s auto-mirror flips them in Arabic without a prop. */}
			<Icon icon={icon} size={13} />
		</button>
	);
}
